import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Check, X } from 'lucide-react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

// Global emitter for confirmation dialogs
type ConfirmListener = (req: ConfirmRequest | null) => void;
let listener: ConfirmListener | null = null;

export const confirmAction = (options: string | ConfirmOptions): Promise<boolean> => {
  return new Promise((resolve) => {
    const parsed: ConfirmOptions = typeof options === 'string' ? { message: options } : options;
    if (listener) {
      listener({
        ...parsed,
        resolve,
      });
    } else {
      // Fallback if modal container is not mounted yet
      resolve(window.confirm(parsed.message));
    }
  });
};

export const ConfirmModalContainer: React.FC = () => {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  useEffect(() => {
    listener = setRequest;
    return () => {
      listener = null;
    };
  }, []);

  if (!request) return null;

  const handleClose = (result: boolean) => {
    request.resolve(result);
    setRequest(null);
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 animate-fadeIn">
      {/* Sharp edges: rounded-none, forest green background: bg-[#006838] */}
      <div className="w-full max-w-md bg-[#006838] text-white border-2 border-[#004d2a] shadow-2xl p-6 rounded-none space-y-5 animate-scaleUp">
        <div className="flex items-start justify-between border-b border-white/20 pb-3">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="w-5 h-5 text-emerald-300 shrink-0" />
            <h3 className="font-extrabold text-base tracking-wide uppercase">
              {request.title || 'Confirm Action'}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="text-white/70 hover:text-white p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="text-white/95 text-sm font-medium leading-relaxed">
          {request.message}
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/20">
          <button
            type="button"
            onClick={() => handleClose(false)}
            className="px-5 py-2.5 bg-black/20 hover:bg-black/30 text-white border border-white/30 text-xs font-bold uppercase tracking-wider rounded-none transition-all focus:outline-none"
          >
            {request.cancelText || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => handleClose(true)}
            className="px-6 py-2.5 bg-white hover:bg-slate-100 text-[#006838] font-black text-xs uppercase tracking-wider rounded-none shadow-md transition-all focus:outline-none flex items-center gap-1.5"
            autoFocus
          >
            <Check className="w-4 h-4 stroke-[3]" />
            <span>{request.confirmText || 'Confirm'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
